import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React from "react";
import AppUser from "@toothfairy/shared-ui/AppUser";
import { useTranslation } from "react-i18next";
import usersAPI from "../../../API/Users/usersAPI";
import usersHelper from "../../../API/Users/usersHelper";
import Constants from "../../../Constants";
import usePolicyVersions from "../../../Hooks/usePolicyVersions";
import { User } from "../../../models";
import { useHistory } from "../../../Router";
import ProfileSettingsScreen from "./ProfileSettingsScreen";

const ProfileSettings = ({ handleUserUpdate, setCurrentView }) => {
  const { state } = AppStateManager.useAppStateManager();
  const { t } = useTranslation();
  const history = useHistory();
  const AppToasty = AppToast.useToast();
  const { signOut } = AppAuth.useAuth();
  const { currentPrivacyVersion } = usePolicyVersions();
  // Prefer the canonical version published by the website
  // (/policy-versions.json); fall back to the hard-coded constant.
  const NEWSLETTER_TERMS_VERSION =
    currentPrivacyVersion || Constants?.newsletterConsent?.termsVersion || "v1";
  const getWorkspaceIndex = (id) => {
    return state?.workspaces?.map((element) => element.id).indexOf(id);
  };

  const { user, updateUser } = AppUser.useUser();
  const { onboardingData } = Constants;

  const formValidationSchema = AppForms.yup.object().shape({
    name: AppForms.yup.string().required().label("Your name"),
    timezone: AppForms.yup.string().required().label("Your timezone"),
    theme: AppForms.yup.string().required().label("Your theme"),
  });

  const handleFormSubmit = async (formValues) => {
    try {
      await usersHelper.updateUser(user.id, [
        { property: "theme", value: formValues.theme },
        { property: "timezone", value: formValues.timezone },
        { property: "username", value: formValues.name },
      ]);
      updateUser({
        ...user,
        theme: formValues.theme,
        timezone: formValues.timezone,
        username: formValues.name,
      });
      AppToasty.show(t("profile.updated"), { type: "success" });
    } catch (error) {
      AppToasty.show(t("profile.updateFailed"), { type: "danger" });
      console.error("Error while updating profile", error);
    }
  };

  const handleNewsletterToggle = async (consent) => {
    try {
      const consentGranted = consent === true;
      const nowTs = Math.floor(Date.now() / 1000);
      const updates = [
        { property: "subscribedToNewsletter", value: consentGranted },
        {
          property: "newsletterSubscriptionStatus",
          value: consentGranted ? "subscribed" : "unsubscribed",
        },
        { property: "newsletterConsentMethod", value: "profile_settings" },
        {
          property: "newsletterConsentTermsVersion",
          value: NEWSLETTER_TERMS_VERSION,
        },
        { property: "newsletterUpdatedAt", value: nowTs },
      ];
      if (consentGranted) {
        updates.push({ property: "newsletterConsentedAt", value: nowTs });
      }
      await usersHelper.updateUser(user.id, updates);
      updateUser({
        ...user,
        subscribedToNewsletter: consentGranted,
        newsletterSubscriptionStatus: consentGranted
          ? "subscribed"
          : "unsubscribed",
        newsletterConsentMethod: "profile_settings",
        newsletterConsentTermsVersion: NEWSLETTER_TERMS_VERSION,
        newsletterUpdatedAt: nowTs,
        ...(consentGranted && { newsletterConsentedAt: nowTs }),
      });
      AppToasty.show(t("newsletter.updated"), { type: "success" });
    } catch (error) {
      AppToasty.show(t("newsletter.updateFailed"), { type: "danger" });
      console.error("Error updating preference", error);
    }
  };

  const handleUserDeletion = async () => {
    try {
      let original = await usersAPI.getUser({ id: user.id });
      original = original?.data?.getUser;
      // Delete user from cognito
      await usersAPI.permanentlyDeleteUser(user.id, original._version);
      setTimeout(() => {
        signOut()
          .then(async () => {
            AppToasty.show(t("legacy.LogoutMessage"), {
              placement: "top",
              type: "success",
            });
            setCurrentView("auth");
            history.push("/");
            window.location.reload();
          })
          .catch((error) => {
            console.error("Auth - logout failed", error);
            AppToasty.show(t("legacy.LogoutFailure"), {
              placement: "top",
              type: "danger",
            });
          });
      }, 2000);
    } catch (error) {
      AppToasty.show(t("profile.deleteFailed"), { type: "danger" });
      console.error("Error while deleting profile", error);
    }
  };

  return (
    <ProfileSettingsScreen
      handleUserDeletion={handleUserDeletion}
      handleFormSubmit={handleFormSubmit}
      handleUserUpdate={handleUserUpdate}
      handleNewsletterToggle={handleNewsletterToggle}
      formValidationSchema={formValidationSchema}
      onboardingData={onboardingData}
      user={user}
      workspace={
        state?.workspaces?.length > 0 &&
        state?.workspaces[getWorkspaceIndex(state.activeWorkspaceID)]
      }
    />
  );
};

export default ProfileSettings;
